import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { peekTeamInvite, redeemTeamInvite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AvatarChip } from "./AvatarChip";

export function TeamInvitePage({ onToast, onRequireAuth, onOpenLeague }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    peekTeamInvite(token)
      .then((d) => { if (!cancelled) setInvite(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Invite link is invalid."); });
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (!user) { onRequireAuth?.(); return; }
    setBusy(true);
    try {
      const r = await redeemTeamInvite(token);
      onToast?.(r.already_on_team ? "✅ You're already on this team." : `🎉 Joined team "${r.team_name}".`);
      onOpenLeague?.(r.league_id);
      navigate("/", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not redeem this invite.";
      setError(msg);
      onToast?.(msg);
    } finally { setBusy(false); }
  };

  return (
    <main className="page-z" data-testid="team-invite-page">
      <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <Link to="/" data-testid="team-invite-back-home" className="inline-flex items-center gap-2 text-xs tracking-widest text-[#a3a3ac] hover:text-[#14b8a6]">
          <i className="fas fa-arrow-left" /> BACK TO ROADMAP
        </Link>

        <div className="mt-6 glass rounded-3xl p-8 text-center neon-glow border-2 border-[#14b8a6]/50">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">🛡️ TEAM INVITE</p>

          {error ? (
            <>
              <h2 data-testid="team-invite-error" className="font-display text-3xl mt-3 text-[#14b8a6]">INVALID</h2>
              <p className="text-sm text-white/70 mt-3">{error}</p>
            </>
          ) : !invite ? (
            <>
              <div className="loader-ring mx-auto mt-8" />
              <p className="text-sm text-white/60 mt-4">Validating link…</p>
            </>
          ) : !invite.valid ? (
            <>
              <h2 data-testid="team-invite-used" className="font-display text-3xl grad-text mt-3">USED</h2>
              <p className="text-sm text-white/70 mt-3">This team invite has already been redeemed.</p>
            </>
          ) : (
            <>
              <div className="mt-4 flex justify-center">
                <AvatarChip avatarId={invite.team_avatar_id ?? 0} size={72} />
              </div>
              <h2 data-testid="team-invite-name" className="font-display text-3xl md:text-4xl grad-text mt-4 leading-tight">
                {invite.team_name}
              </h2>
              <p className="text-xs text-[#a3a3ac] mt-2">
                team inside <span className="text-white">{invite.league_name}</span>
                {invite.league_slug ? <> · leaguehub.co/{invite.league_slug}</> : null}
              </p>
              <p className="text-sm text-white/70 mt-4">
                The team manager wants you on the roster. One click — you're in.
                {!user && " We'll auto-add you to the league too if you're not already in it."}
              </p>
              <p className="text-[11px] text-white/50 mt-2">
                {invite.members_count} current member{invite.members_count === 1 ? "" : "s"}
              </p>
              <button
                onClick={accept}
                disabled={busy}
                data-testid="team-invite-accept-btn"
                className="mt-6 w-full btn-neon rounded-2xl py-4 text-sm tracking-[3px] disabled:opacity-40"
              >
                {busy ? "JOINING..." : "🛡️ JOIN THE TEAM"}
              </button>
              {!user && (
                <p className="text-[11px] text-white/50 mt-3">
                  Sign-in required — we'll bring you back here right after.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default TeamInvitePage;
